import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  LEVEL_LABEL,
  LEVEL_PARENT,
  MASTER_AGENT_TYPES,
  MATRIX_COMMISSION_PERMISSION,
  RECORD_STATUSES,
  US_STATES,
  accessFlagsOf,
  userCreateSchema,
  userUpdateSchema,
} from '@app/shared';
import type { UserCreateInput, UserDto, UserLevel, UserUpdateInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import {
  SelectInput,
  TextInput,
  YesNoInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { useAuth } from '../auth/AuthProvider';
import {
  useCreateUser,
  useGroupOptions,
  useMatrixOptions,
  useUpdateUser,
  useUserOptions,
} from './api';

// The reference writes a state as "Alabama (AL)".
const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${name} (${code})`,
}));

/** Heading of each level's form on the reference: "Add <this> Details". */
export const USER_FORM_TITLE: Record<UserLevel, string> = {
  admin: 'Administrator',
  master: 'Master Agent',
  distributor: 'Distributor',
  retailer: 'Retailer',
  employee: 'Employee',
};

const PARENT_LABEL: Partial<Record<UserLevel, string>> = {
  master: 'Master agent name',
  distributor: 'Distributor name',
  retailer: 'Retailer name',
};

/** The levels above `level`, top first: the selects of "Assignment Details". */
function levelsAbove(level: UserLevel): UserLevel[] {
  const chain: UserLevel[] = [];
  for (let up = LEVEL_PARENT[level]; up; up = LEVEL_PARENT[up]) chain.unshift(up);
  return chain;
}

interface Props {
  open: boolean;
  level: UserLevel;
  /** Present when editing. */
  user?: UserDto;
  onClose: () => void;
}

type FormValues = UserCreateInput & UserUpdateInput;

const SectionTitle = ({ children }: { children: string }) => (
  <Typography
    variant="subtitle2"
    component="h3"
    sx={{ gridColumn: '1 / -1', borderBottom: 1, borderColor: 'divider', pb: 0.5, mt: 0.5 }}
  >
    {children}
  </Typography>
);

/**
 * Add or edit a user of one hierarchy level, with the four sections of the reference's add
 * forms. The reference opens them as pages; here they are a dialog, like every other form.
 */
export function UserFormDialog({ open, level, user, onClose }: Props) {
  const notify = useNotify();
  const { can } = useAuth();
  const editing = Boolean(user);
  const above = levelsAbove(level);
  const matrixCode = MATRIX_COMMISSION_PERMISSION[level];
  const canMatrix = Boolean(matrixCode && can(matrixCode));

  // The selects above the direct parent only narrow the next one; they are not sent.
  const [picked, setPicked] = useState<Partial<Record<UserLevel, string>>>({});
  const [showPassword, setShowPassword] = useState(false);

  const groups = useGroupOptions(open);
  const matrices = useMatrixOptions(open && canMatrix);
  const creating = open && !editing;
  const masters = useUserOptions('master', undefined, creating && above.includes('master'));
  const distributors = useUserOptions(
    'distributor',
    picked.master,
    creating && above.includes('distributor') && Boolean(picked.master),
  );
  const retailers = useUserOptions(
    'retailer',
    picked.distributor,
    creating && above.includes('retailer') && Boolean(picked.distributor),
  );
  const optionsOf: Partial<Record<UserLevel, typeof masters.data>> = {
    master: masters.data,
    distributor: distributors.data,
    retailer: retailers.data,
  };

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const { control, handleSubmit, reset, setError, setValue } = useForm<FormValues>({
    resolver: zodResolver(editing ? userUpdateSchema : userCreateSchema),
  });

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setShowPassword(false);
    const flags = Object.fromEntries(
      accessFlagsOf(level).map(([key]) => [key, user ? user[key] : false]),
    );
    reset(
      user
        ? ({
            firstName: user.firstName,
            lastName: user.lastName,
            companyName: user.companyName,
            agentType: user.agentType,
            email: user.email,
            phone: user.phone,
            address: user.address,
            groupId: user.group?.id ?? '',
            commissionMatrixId: user.commissionMatrix?.id ?? null,
            ...flags,
            status: user.status,
            password: undefined,
          } as FormValues)
        : ({
            loginId: '',
            password: '',
            firstName: '',
            lastName: '',
            companyName: '',
            agentType: null,
            email: '',
            phone: '',
            address: { line1: '', line2: '', city: '', state: '', zip: '' },
            groupId: '',
            commissionMatrixId: null,
            ...flags,
            status: 'Active',
            level,
            parentId: null,
          } as FormValues),
    );
  }, [open, user, level, reset]);

  const submit = handleSubmit(async (values) => {
    // Without the matrix permission the field is read-only and is left out of the request.
    const { password, commissionMatrixId, ...rest } = values;
    const input = {
      ...rest,
      ...(canMatrix ? { commissionMatrixId } : {}),
      ...(password ? { password } : {}),
    };
    try {
      if (user) {
        await updateUser.mutateAsync({ id: user.id, input });
        notify.success(`${LEVEL_LABEL[level]} updated.`);
      } else {
        await createUser.mutateAsync(input as UserCreateInput);
        notify.success(`${LEVEL_LABEL[level]} created.`);
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const pick = (at: UserLevel, id: string) => {
    // Choosing again higher up empties everything below it.
    const kept = above.slice(0, above.indexOf(at));
    setPicked((p) => ({ ...Object.fromEntries(kept.map((l) => [l, p[l]])), [at]: id }));
    setValue('parentId', null);
  };
  const userOptions = (at: UserLevel) =>
    (optionsOf[at] ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.loginId})` }));

  const title = USER_FORM_TITLE[level];
  return (
    <FormDialog
      open={open}
      title={`${editing ? 'Edit' : 'Add'} ${title} Details`}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createUser.isPending || updateUser.isPending}
      maxWidth="md"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        <SectionTitle>Assignment Details</SectionTitle>
        <SelectInput
          control={control}
          name="groupId"
          label={level === 'employee' ? 'User Group' : 'User group type'}
          required
          // A group the caller's group may not give ("Assign Group") is shown but cannot be chosen.
          options={(groups.data ?? []).map((g) => ({
            value: g.id,
            label: g.name,
            disabled: g.assignable === false,
          }))}
        />
        {level === 'master' ? (
          <>
            <SelectInput
              control={control}
              name="agentType"
              label="Type"
              required
              emptyLabel="Select type"
              options={toOptions(MASTER_AGENT_TYPES)}
            />
            <TextInput
              control={control}
              name="companyName"
              label="Agent company name"
              required
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </>
        ) : null}
        {editing
          ? above.map((at) => (
              <TextField
                key={at}
                label={PARENT_LABEL[at]}
                value={user!.chain[at as 'master' | 'distributor' | 'retailer']?.name ?? ''}
                disabled
              />
            ))
          : above.map((at, i) =>
              i === above.length - 1 ? (
                <SelectInput
                  key={at}
                  control={control}
                  name="parentId"
                  label={PARENT_LABEL[at]!}
                  required
                  options={userOptions(at)}
                />
              ) : (
                <TextField
                  key={at}
                  select
                  required
                  label={PARENT_LABEL[at]}
                  value={picked[at] ?? ''}
                  onChange={(e) => pick(at, e.target.value)}
                >
                  {userOptions(at).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              ),
            )}

        <SectionTitle>Personal Details</SectionTitle>
        <TextInput
          control={control}
          name="firstName"
          label="First Name"
          required
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <TextInput
          control={control}
          name="lastName"
          label="Last Name"
          required
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        {editing ? (
          <TextField label="Login Id" value={user!.loginId} disabled />
        ) : (
          <TextInput
            control={control}
            name="loginId"
            label="Login Id"
            required
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 40 } }}
          />
        )}
        <Box>
          <TextInput
            control={control}
            name="password"
            label={editing ? 'New password' : 'Password'}
            required={!editing}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            helperText={
              editing
                ? 'Leave empty to keep the current password'
                : 'At least 8 characters with a letter and a digit'
            }
          />
          <FormControlLabel
            label="Show"
            control={
              <Checkbox
                size="small"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
            }
          />
        </Box>
        <TextInput
          control={control}
          name="email"
          label="Email Address"
          required
          type="email"
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <TextInput
          control={control}
          name="phone"
          label="Phone Number"
          slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
        />

        <SectionTitle>Address Details</SectionTitle>
        <TextInput
          control={control}
          name="address.line1"
          label="Address 1"
          required
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <TextInput
          control={control}
          name="address.line2"
          label="Address 2"
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <TextInput
          control={control}
          name="address.zip"
          label="Zip"
          required
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <TextInput control={control} name="address.city" label="City" required />
        <SelectInput
          control={control}
          name="address.state"
          label="State"
          required
          options={STATE_OPTIONS}
        />

        <SectionTitle>Commission &amp; Access</SectionTitle>
        {matrixCode ? (
          <SelectInput
            control={control}
            name="commissionMatrixId"
            label={`Matrix commission (${matrixCode})`}
            emptyLabel="No Commission"
            disabled={!canMatrix}
            helperText={
              canMatrix ? undefined : `Permission required to assign commission matrix to ${title}`
            }
            options={[
              // A matrix that was made Inactive stays visible on the user it was given to.
              ...(user?.commissionMatrix &&
              !(matrices.data ?? []).some((m) => m.id === user.commissionMatrix!.id)
                ? [{ value: user.commissionMatrix.id, label: user.commissionMatrix.name }]
                : []),
              ...(matrices.data ?? []).map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
        ) : null}
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
        {accessFlagsOf(level).map(([key, label]) => (
          <YesNoInput key={key} control={control} name={key} label={label} />
        ))}
        {editing ? null : (
          <Alert severity="info" sx={{ gridColumn: '1 / -1', py: 0 }}>
            The matrix and the Yes / No options are kept with the user. Commission is not calculated
            and no option changes what the user can do yet.
          </Alert>
        )}
      </Box>
    </FormDialog>
  );
}
